import { useTranslation } from 'react-i18next';
import SecondaryButton from '../SecondaryButton/SecondaryButton';
import { Icon } from '../Icon/Icon';
import { ResponsiveMenu } from '../ResponsiveMenu/ResponsiveMenu';
import MenuContainer from '../MenuContainer/MenuContainer';
import LogoutButton from '../LogOutButton/LogOutButton';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import store from '../../assets/images/store.svg';
import catalogues from '../../assets/images/catalogue.svg';
import settings from '../../assets/images/settings.svg';
import orders from '../../assets/images/orders.svg';
import box from '../../assets/images/box.svg';
import chartBar from '../../assets/images/chart-bar.svg';
import { useSelector } from 'react-redux';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import { useState } from 'react';
import { Modal } from '../Modal/Modal';
import check from '../../assets/images/check.svg';
import { BolderText } from '../BolderText/BolderText';
import LoginFlow from '../LoginFlow/LoginFlow';
import ActivePricePlan from '../ActivePricePlan/ActivePricePlan';
import { useNavigate } from 'react-router-dom';
import FlyoutMenu from '../FlyoutMenu/FlyoutMenu';
import calendarIcon from '../../assets/images/calendar.svg';
import { AdminRestricted } from '../AdminRestricted/AdminRestricted';
import PlainTextButton from '../PlainTextButton/PlainTextButton';
import { useOnboarding } from '../../hooks/useOnboarding';
import secondHandIcon from '../../assets/images/shopping-bag.svg';
import formsIcon from '../../assets/images/document-text.svg';
import secondHandContractsIcon from '../../assets/images/document-plus.svg';
import star from '../../assets/images/star.svg';
import HelpContactDialog from '../HelpContact/HelpContactDialog';
import { CrispChatButton } from '../Crisp/CrispChatButton';
import { NewsFlyout } from '../News/NewsFlyout';
import { filterMenuItemsByModules } from '../../shared/tenantModules';

function MainMenu() {
  const { t } = useTranslation();
  const isLoggedIn = useSelector((state: any) => state.settings.value.isLoggedIn);
  const enabledModules = useSelector((state: any) => state.settings.value.enabledModules);
  const [displayLoginSignUpFlow, setDisplayLoginSignUpFlow] = useState(false);
  const navigate = useNavigate();
  const { onboardingStatus, skipStep } = useOnboarding(isLoggedIn);
  const allStepsCompleted = onboardingStatus && Object.values(onboardingStatus.status).every(step => step);

  const goToLoginPage = () => {
    navigate(`/login`);
  };

  const menuItems: any[] = [
    { text: t('menu.store')!, href: '/', isDisabled: false, flags: ['see-meetings', 'see-contracts'], name: 'Store', module: 'STORE',
    icon: <><Icon url={store} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('menu.calendar')!, href: '/calendar', flags: ['see-meetings'], isDisabled: false, name: 'Calendar'},
        { text: t('menu.calendar_app'), href: '/bookings', flags: ['create-meetings'], isDisabled: false, name: 'Bookings'},
        { text: t('menu.sales')!, href: '/sales', flags: ['see-contracts'], isDisabled: false, name: 'Sales'},
        { text: t('menu.customers')!, href: '/customers', flags: ['see-customers'], isDisabled: false, name: 'Customers CRM'},
      ] 
    },
    { 
      text: t('menu.orders')!, href: '/orders', flags: ['see-orders'], name: 'Orders', module: 'B2B_ORDERS',
      icon: <><Icon url={orders} />&nbsp;&nbsp;</>,  
    },
    { text: t('admin.menu.collections')!, href: '/catalogues', isDisabled: false, flags: ['see-marketing-collections'], name: 'Catalogues', icon: <><Icon url={catalogues} />&nbsp;&nbsp;</> },
    // { text: t('menu.marketing')!, href: '/', isDisabled: false, flags: ['see-marketing-collections', 'see-leads'], name: 'Marketing', 
    // icon: <><Icon url={catalogues} />&nbsp;&nbsp;</>, 
    //   submenuItems: [
    //     { text: t('admin.menu.collections')!, href: '/catalogues', isDisabled: false, flags: ['see-marketing-collections'], name: 'Catalogues' },
    //     { text: t('menu.leads')!, href: '/leads', isDisabled: false, flags: ['see-leads'], name: 'Leads' },
    //     { text: t('menu.tablet')!, href: '/tablet', isDisabled: true, flags: ['see-marketing-collections'], name: 'Tablet' },
    //   ] 
    // },
    // { text: t('menu.find_partners')!, href: '/find-partners', isDisabled: false, flags: ['see-orders', 'see-partners'], name: 'Find Partners', 
    //   icon: <><Icon url={partnersIcon} />&nbsp;&nbsp;</> 
    // },
    { text: t('menu.production')!, href: '/production', flags: ['see-providers', 'see-orders', 'see-self-orders'], name: 'Production', module: 'PRODUCTION',
    icon: <><Icon url={box} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('production.menu.orders')!, href: '/production-orders', isDisabled: false, flags: ['see-orders', 'see-self-orders'], name: 'Orders'},
        { text: t('production.menu.providers')!, href: '/providers', isDisabled: false, flags: ['see-providers'], name: 'Providers'},
        { text: t('production.menu.stock')!, href: '/stock', isDisabled: false, flags: ['see-stock'], name: 'Stock'},
        { text: t('production.menu.dresses')!, href: '/inventory', isDisabled: false, flags: ['see-dresses'], name: 'Item Plans'}
      ] 
    },
    { text: t('menu.secondHand')!, href: '/second-hand', flags: ['see-commission-contracts'], name: 'Second Hand', module: 'SECOND_HAND',
      icon: <><Icon url={secondHandIcon} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('menu.secondHand.contracts')!, href: '/commission/drops', isDisabled: false, flags: ['see-commission-contracts'], name: 'Contracts', icon: <><Icon url={secondHandContractsIcon} />&nbsp;&nbsp;</>},
        { text: t('menu.secondHand.forms')!, href: '/commission/settings', isDisabled: false, flags: ['see-commission-contracts'], name: 'Forms', icon: <><Icon url={formsIcon} />&nbsp;&nbsp;</>},
      ] 
    },
    { text: t('menu.stats')!, href: '/', isDisabled: false, flags: ['see-employees'], name: 'Statistics', 
      icon: <><Icon url={chartBar} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('stats.meetings_bookings.title')!, href: '/insights/meetings', isDisabled: false, flags: ['see-employees'], name: 'Stats Meetings', module: 'STORE' },
        { text: t('menu.stats_orders')!, href: '/insights/orders', isDisabled: false, flags: ['see-employees'], name: 'Stats Orders', module: 'PRODUCTION' },
        { text: t('settings.menu.payments')!, href: '/insights/payments', isDisabled: false, flags: ['see-employees'], name: 'Stats Payments', module: 'B2B_ORDERS' },
        { text: t('menu.secondHand')!, href: '/insights/commission-contracts', isDisabled: false, flags: ['see-employees'], name: 'Commission Contracts Analytics', module: 'SECOND_HAND' },
      ] 
    },
    { text: t('menu.services')!, href: '/benefits', isDisabled: false, flags: [], name: 'Services', 
      icon: <><Icon url={star} />&nbsp;&nbsp;</>
    },
    { text: t('menu.admin')!, href: '/', flags: ['see-partners'], name: 'Admin', 
    icon: <><Icon url={settings} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('admin.menu.partners')!, href: '/partners', flags: ['see-partners'], isDisabled: false, name: 'Partners'},
        { text: t('admin.menu.employees')!, href: '/employees', flags: ['see-employees'], name: 'Employees' },
      ] 
    },
    { text: t('menu.settings')!, href: '/settings', flags: ['see-employees'], isDisabled: false, name: 'Settings', 
      icon: <><Icon url={settings} />&nbsp;&nbsp;</>
    }
  ];

  const visibleMenuItems = filterMenuItemsByModules(menuItems, enabledModules);


  const onboardingItems = [
    {
      headline: t("onboarding.menu.store"),
      text: 
        <>
          <div className="text-sm min-w-[300px]">{t("onboarding.menu.store.description")}</div>
          { !onboardingStatus?.status.setupStore ?
            <div className="space-x-3">
              <PlainTextButton text={t("global.mark_completed")!} onClick={() => skipStep('setupStore')} actionName="Complete Onboarding Settings"/>
              <SecondaryButton text={t("global.go")!} fn={() => navigate('/settings')} actionName="Onboarding Settings"/>
            </div>
            : null
          }
        </>,
      href: '#',
      onClick: () => navigate('/settings'),
      isDisabled: onboardingStatus?.status.setupStore,
      icon: <><Icon url={settings} />&nbsp;&nbsp;</>,
      styles: onboardingStatus?.status.setupStore
        ? { backgroundColor: '#f3f4f6', textDecoration: 'line-through', color: 'gray' }
        : { backgroundColor: '#fff1f2' },
    },
    {
      headline: t("onboarding.menu.calendar"),
      text: 
        <>
          <div className="text-sm min-w-[300px]">{t("onboarding.menu.calendar.description")}</div>
          { !onboardingStatus?.status.startCalendar ?
            <div className="space-x-3">
              <PlainTextButton text={t("global.mark_completed")!} onClick={() => skipStep('startCalendar')} actionName="Complete Onboarding Calendar"/>
              <SecondaryButton text={t("global.go")!} fn={() => navigate('/bookings')} actionName="Onboarding Calendar"/>
            </div>
            : null
          }
        </>,
      href: '#',
      onClick: () => navigate('/bookings'),
      icon: <><Icon url={calendarIcon} />&nbsp;&nbsp;</>,
      isDisabled: onboardingStatus?.status.startCalendar,
      styles: onboardingStatus?.status.startCalendar
        ? { backgroundColor: '#f3f4f6', textDecoration: 'line-through', color: 'gray' }
        : { backgroundColor: '#fff1f2' },
    },
    {
      headline: t("onboarding.menu.catalog"),
      text: 
        <>
          <div className="text-sm min-w-[300px]">{t("onboarding.menu.catalog.description")}</div>
          { !onboardingStatus?.status.publishCatalog ?
            <div className="space-x-3">
              <PlainTextButton text={t("global.mark_completed")!} onClick={() => skipStep('publishCatalog')} actionName="Complete Onboarding Catalogues"/>
              <SecondaryButton text={t("global.go")!} fn={() => navigate('/catalogues')} actionName="Onboarding Catalogues"/>
            </div>
            : null
          }
        </>,
      href: '#',
      onClick: () => navigate('/catalogues'),
      icon: <><Icon url={catalogues} />&nbsp;&nbsp;</>,
      isDisabled: onboardingStatus?.status.publishCatalog,
      styles: onboardingStatus?.status.publishCatalog
        ? { backgroundColor: '#f3f4f6', textDecoration: 'line-through', color: 'gray' }
        : { backgroundColor: '#fff1f2' },
    },
    {
      headline: t("onboarding.menu.order"),
      text: 
        <>
          <div className="text-sm min-w-[300px]">{t("onboarding.menu.order.description")}</div>
          { !onboardingStatus?.status.registerOrder ?
            <div className="space-x-3">
              <PlainTextButton text={t("global.mark_completed")!} onClick={() => skipStep('registerOrder')} actionName="Complete Onboarding Orders"/>
              <SecondaryButton text={t("global.go")!} fn={() => navigate('/orders')} actionName="Onboarding Orders"/>
            </div>
            : null
          }
        </>,
      href: '#',
      onClick: () => navigate('/orders'),
      icon: <><Icon url={orders} />&nbsp;&nbsp;</>,
      isDisabled: onboardingStatus?.status.registerOrder,
      styles: onboardingStatus?.status.registerOrder
        ? { backgroundColor: '#f3f4f6', textDecoration: 'line-through', color: 'gray' }
        : { backgroundColor: '#fff1f2' },
    },
  ];

  return (
    <MenuContainer
      mobileMenuButton={
        isLoggedIn ? (
          <ResponsiveMenu
            menuItems={visibleMenuItems}
          />
        ) : undefined
      }
    >
      { isLoggedIn ? <>
        {/* All other elements */}
        <CrispChatButton />
        <HelpContactDialog />
        <AdminRestricted >
          {
            <>
              {!allStepsCompleted &&
              <FlyoutMenu
                menuItems={onboardingItems}
                trigger={
                  <button
                    type="button"
                    className="max-w-sm font-sans font-bold tracking-tight bg-gradient-to-r from-orange-300 via-rose-400 to-pink-400 rounded-lg duration-1000 px-3 py-1 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl animate-slow-pulse"
                    data-dd-action-name="Onboarding Menu (Desktop)"
                  >
                    {t("onboarding.menu")}
                  </button>
                }
                mobileTrigger={
                  <button
                    type="button"
                    className="max-w-sm text-sm font-sans font-bold tracking-tight bg-gradient-to-r from-orange-300 via-rose-400 to-pink-400 rounded-lg duration-1000 px-3 py-1 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl animate-slow-pulse"
                    data-dd-action-name="Onboarding Menu (Mobile)"
                  >
                    {t("onboarding.menu.mobile")}
                  </button>
                }
              /> }
            </>
          }
        </AdminRestricted>
        <NewsFlyout />
        <ActivePricePlan />
        <LanguageSwitcher />
        <LogoutButton />
      </> : 
      <>
        {/* All other elements */}
        <HelpContactDialog />
        <LanguageSwitcher />
        <PrimaryButton text={t("guest.login.cta")!} fn={() => goToLoginPage()} />

        { displayLoginSignUpFlow && 
          <Modal canClose modalTitle={t("guest.login.form.title")} onClose={() => setDisplayLoginSignUpFlow(false)}>
            <>
              <p>
                { t("guest.login.form.description") }.
              </p>
              <p><Icon url={check}></Icon>&nbsp; <BolderText text={t("guest.login.form.reason_1")!}/></p>
              <p><Icon url={check}></Icon>&nbsp; <BolderText text={t("guest.login.form.reason_2")!}/></p>
              <p><Icon url={check}></Icon>&nbsp; <BolderText text={t("guest.login.form.reason_3")!}/></p>
              <p><Icon url={check}></Icon>&nbsp; ... { t("guest.login.form.reason_others") } </p>
              <br/>
              <LoginFlow />
            </>
          </Modal> 
        }
      </> }
    </MenuContainer>
  );
}

export default MainMenu;
